// src/contexts/ClassroomContext.js - Enhanced version
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../services/supabase';

const ClassroomContext = createContext();

export const useClassroom = () => {
  const context = useContext(ClassroomContext);
  if (!context) {
    throw new Error('useClassroom must be used within ClassroomProvider');
  }
  return context;
};

export const ClassroomProvider = ({ children }) => {
  const [students, setStudents] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [currentClassroom, setCurrentClassroom] = useState(null);
  const [whiteboardData, setWhiteboardData] = useState(null);
  const [currentActivity, setCurrentActivity] = useState(null);
  const [roomId, setRoomId] = useState(null);

  // Create new classroom
  const createClassroom = async (classroomData) => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured. Using mock data.');
      const mockData = { id: Date.now(), ...classroomData, created_at: new Date().toISOString() };
      setCurrentClassroom(mockData);
      setRoomId(mockData.id);
      return mockData;
    }

    try {
      const { data, error } = await supabase
        .from('myclassboard_classrooms')
        .insert([classroomData])
        .select()
        .single();

      if (error) throw error;
      
      setCurrentClassroom(data);
      setRoomId(data.id);
      return data;
    } catch (error) {
      console.error('Error creating classroom:', error);
      throw error;
    }
  };

  // Load classrooms
  const loadClassrooms = async () => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured. Using mock data.');
      const mockData = [];
      setClassrooms(mockData);
      return mockData;
    }

    try {
      const { data, error } = await supabase
        .from('myclassboard_classrooms')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setClassrooms(data || []);
      return data;
    } catch (error) {
      console.error('Error loading classrooms:', error);
      throw error;
    }
  };

  // Join classroom
  const joinClassroom = async (roomId, studentData) => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured. Using mock data.');
      const mockData = { id: Date.now(), ...studentData, room_id: roomId, role: 'student' };
      setRoomId(roomId);
      setStudents(prev => [...prev, mockData]);
      return mockData;
    }

    try {
      const { data, error } = await supabase
        .from('myclassboard_students')
        .insert([{ ...studentData, room_id: roomId, role: 'student' }])
        .select()
        .single();

      if (error) throw error;
      
      setRoomId(roomId);
      return data;
    } catch (error) {
      console.error('Error joining classroom:', error);
      throw error;
    }
  };

  // Update student role
  const updateStudentRole = async (studentId, role) => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured. Using mock data.');
      setStudents(prev => 
        prev.map(student => 
          student.id === studentId ? { ...student, role } : student
        )
      );
      return { id: studentId, role };
    }

    try {
      const { data, error } = await supabase
        .from('myclassboard_students')
        .update({ role })
        .eq('id', studentId)
        .select()
        .single();

      if (error) throw error;
      
      setStudents(prev => 
        prev.map(student => 
          student.id === studentId ? { ...student, role } : student
        )
      );
      
      return data;
    } catch (error) {
      console.error('Error updating student role:', error);
      throw error;
    }
  };

  // Real-time subscriptions
  useEffect(() => {
    if (!roomId) return;

    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured. Skipping real-time subscriptions.');
      return;
    }

    const studentsSubscription = supabase
      .channel('students')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'myclassboard_students', filter: `room_id=eq.${roomId}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setStudents(prev => [...prev, payload.new]);
          } else if (payload.eventType === 'UPDATE') {
            setStudents(prev => 
              prev.map(student => 
                student.id === payload.new.id ? payload.new : student
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setStudents(prev => 
              prev.filter(student => student.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe();

    // Load initial students
    const loadStudents = async () => {
      const { data, error } = await supabase
        .from('myclassboard_students')
        .select('*')
        .eq('room_id', roomId);
      
      if (!error && data) {
        setStudents(data);
      }
    };

    loadStudents();

    return () => {
      supabase.removeChannel(studentsSubscription);
    };
  }, [roomId]);

  // Previous functions remain the same...
  const addStudent = async (studentData) => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured. Using mock data.');
      const mockData = { id: Date.now(), ...studentData, room_id: roomId, role: 'student' };
      setStudents(prev => [...prev, mockData]);
      return mockData;
    }

    const { data, error } = await supabase
      .from('myclassboard_students')
      .insert([{ ...studentData, room_id: roomId, role: 'student' }]);
    
    if (error) console.error('Error adding student:', error);
    return data;
  };

  const updateStudentStatus = async (studentId, status) => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured. Using mock data.');
      setStudents(prev => 
        prev.map(student => 
          student.id === studentId ? { ...student, status } : student
        )
      );
      return { id: studentId, status };
    }

    const { data, error } = await supabase
      .from('myclassboard_students')
      .update({ status })
      .eq('id', studentId);
    
    if (error) console.error('Error updating student:', error);
    return data;
  };

  const removeStudent = async (studentId) => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured. Using mock data.');
      setStudents(prev => prev.filter(student => student.id !== studentId));
      return { id: studentId };
    }

    const { data, error } = await supabase
      .from('myclassboard_students')
      .delete()
      .eq('id', studentId);
    
    if (error) console.error('Error removing student:', error);
    return data;
  };

  const value = {
    students,
    classrooms,
    currentClassroom,
    whiteboardData,
    currentActivity,
    roomId,
    setRoomId,
    createClassroom,
    loadClassrooms,
    joinClassroom,
    addStudent,
    updateStudentStatus,
    updateStudentRole,
    removeStudent,
    setWhiteboardData,
    setCurrentActivity
  };

  return (
    <ClassroomContext.Provider value={value}>
      {children}
    </ClassroomContext.Provider>
  );
};